import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import {
  Employee,
  EmployeeByEmail,
  EmployeeByRoleGridify,
  EmployeeGridify,
  EmployeeInfo,
  EmployeeRequestPayload,
  EmployeeWithRolesByIdentityIdDetails,
  EmployeesByIdentityId,
} from '@/features/employees/types';
import { FiltersGrid, Pagination } from '@/features/types';
import {
  cachedItemByIdSelector,
  infiniteQueryOptions,
  infiniteQueryOptionsFn,
} from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';
import { buildGridifySearchParams } from '@/utils/buildGridifySearchParams';

const normalizeEmployeeResponse = (data: Employee) => {
  if (data?.email === null) {
    data.email = '';
  }
  if (data?.phoneNumber === null) {
    data.phoneNumber = '';
  }
  if (data?.startDate === null) {
    data.startDate = '';
  }
  if (data?.endDate === null) {
    data.endDate = '';
  }
  return data;
};

export const employeesApi = createApi({
  reducerPath: 'employeesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/employees`,
  }),
  tagTypes: [
    'Employees',
    'Employee',
    'EmployeesByRole',
    'EmployeeByEmail',
    'EmployeesAll',
    'AllEmployeeRole',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => {
    return {
      getEmployeesI: builder.infiniteQuery<
        EmployeeGridify[],
        FiltersGrid & {
          roleId?: string;
          organizationId?: string;
          areMembersOfStructuralSubdivisions?: boolean;
          actDate?: string;
        },
        Pagination
      >({
        infiniteQueryOptions,
        query: ({ queryArg, pageParam }) => {
          const queryParams = buildGridifySearchParams({
            ...queryArg,
            ...pageParam,
          });

          return {
            url: `/query?${queryParams}`,
          };
        },
        providesTags: () => ['Employees'],
      }),
      getEmployeesByRoleI: builder.infiniteQuery<
        EmployeeByRoleGridify,
        FiltersGrid & {
          organizationId: string;
          roleId?: string;
          name?: string;
        },
        Pagination
      >({
        infiniteQueryOptions,
        query: ({ queryArg, pageParam }) => ({
          url: `/by-role`,
          params: { ...queryArg, ...pageParam },
        }),
        providesTags: () => ['EmployeesByRole'],
      }),
      getEmployeesByRole: builder.query<
        EmployeeByRoleGridify[],
        FiltersGrid & {
          organizationId: string;
          roleId?: string;
          name?: string;
        }
      >({
        query: params => ({
          url: `/by-role`,
          params,
        }),
        providesTags: () => ['EmployeesByRole'],
      }),
      getEmployeesAccessibleByOrganization: builder.infiniteQuery<
        EmployeeGridify,
        FiltersGrid & {
          organizationId: string;
          name?: string;
        },
        Pagination
      >({
        infiniteQueryOptions: infiniteQueryOptionsFn({ pageSize: 500 }),
        query: ({ queryArg, pageParam }) => ({
          url: `/accessible/for-organization`,
          params: { ...queryArg, ...pageParam },
        }),
        providesTags: () => ['Employees'],
      }),
      getEmployeeById: builder.query<Employee, string>({
        query: id => ({
          url: `/${id}`,
        }),
        transformResponse: normalizeEmployeeResponse,
        providesTags: (_, __, id) => [{ type: 'Employee', id }],
      }),
      createEmployee: builder.mutation<Employee, EmployeeRequestPayload>({
        query: data => ({
          url: ``,
          method: 'POST',
          data,
        }),
        invalidatesTags: result => [
          'Employees',
          { type: 'Employee', id: result?.id },
        ],
      }),
      updateEmployeeById: builder.mutation<Employee, EmployeeRequestPayload>({
        query: data => ({
          url: `/${data.id}`,
          method: 'PUT',
          data,
        }),
        invalidatesTags: result => [
          'Employees',
          { type: 'Employee', id: result?.id },
        ],
      }),
      deleteEmployeeById: builder.mutation<Employee, string>({
        query: id => ({
          url: `/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_, __, id) => ['Employees', { type: 'Employee', id }],
      }),
      activateEmployeeById: builder.mutation<Employee, string>({
        query: id => ({
          url: `/${id}/send-activation-request`,
          method: 'POST',
        }),
        invalidatesTags: (_, __, id) => ['Employees', { type: 'Employee', id }],
      }),
      resetEmployeePasswordById: builder.mutation<Employee, string>({
        query: id => ({
          url: `/${id}/remove-password`,
          method: 'POST',
        }),
        invalidatesTags: (_, __, id) => ['Employees', { type: 'Employee', id }],
      }),
      deactivateEmployeeById: builder.mutation<
        Employee,
        {
          id: string;
          endDate: string;
        }
      >({
        query: ({ id, endDate }) => ({
          url: `/${id}/deactivate`,
          method: 'POST',
          data: { endDate },
        }),
        invalidatesTags: (_, __, { id }) => [
          'Employees',
          'AllEmployeeRole',
          { type: 'Employee', id },
        ],
      }),
      getEmployeeByEmail: builder.query<
        EmployeeByEmail,
        {
          email: string;
        }
      >({
        query: params => ({
          url: `/by-email`,
          params,
        }),
        providesTags: () => ['EmployeeByEmail'],
      }),
      getEmployeesAllI: builder.infiniteQuery<
        EmployeeInfo[],
        FiltersGrid,
        Pagination
      >({
        infiniteQueryOptions,
        query: ({ queryArg, pageParam }) => ({
          url: `/all-with-unique-identity`,
          params: { ...queryArg, ...pageParam },
        }),
        providesTags: () => ['EmployeesAll'],
      }),
      getEmployeeByIdentityDetails: builder.query<
        EmployeeWithRolesByIdentityIdDetails,
        {
          identityId: string;
        }
      >({
        query: params => ({
          url: `/identity-details`,
          params,
        }),
        providesTags: () => ['AllEmployeeRole'],
      }),
      getEmployeesByIdentityIdI: builder.infiniteQuery<
        EmployeesByIdentityId[],
        FiltersGrid,
        Pagination
      >({
        infiniteQueryOptions,
        query: ({ queryArg, pageParam }) => ({
          url: `/by-identity-query`,
          params: { ...queryArg, ...pageParam },
        }),
        providesTags: () => ['AllEmployeeRole'],
      }),
      activateEmployeeByIdentityId: builder.mutation<
        Employee,
        {
          identityId: string;
        }
      >({
        query: params => ({
          url: `/send-activation-request-by-identity`,
          method: 'POST',
          params,
        }),
        invalidatesTags: () => ['AllEmployeeRole'],
      }),
      resetPasswordEmployeeByIdentityId: builder.mutation<
        Employee,
        {
          identityId: string;
        }
      >({
        query: params => ({
          url: `/remove-password-by-identity`,
          method: 'POST',
          params,
        }),
        invalidatesTags: () => ['AllEmployeeRole'],
      }),
      deactivateEmployeeByIdentityId: builder.mutation<
        Employee,
        {
          identityId: string;
        }
      >({
        query: params => ({
          url: `/deactivate-by-identity`,
          method: 'POST',
          params,
        }),
        invalidatesTags: () => ['AllEmployeeRole'],
      }),
    };
  },
});

export const {
  useGetEmployeesByRoleQuery,
  useGetEmployeeByIdQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeByIdMutation,
  useActivateEmployeeByIdMutation,
  useResetEmployeePasswordByIdMutation,
  useDeactivateEmployeeByIdMutation,
  useLazyGetEmployeeByEmailQuery,
  useGetEmployeeByIdentityDetailsQuery,
  useActivateEmployeeByIdentityIdMutation,
  useResetPasswordEmployeeByIdentityIdMutation,
  useDeactivateEmployeeByIdentityIdMutation,
} = employeesApi;

export const useGetEmployeesInfiniteQuery = normalizeInfiniteQueryData(
  employeesApi.useGetEmployeesIInfiniteQuery,
);

export const useGetEmployeesByIdentityInfiniteQuery =
  normalizeInfiniteQueryData(
    employeesApi.useGetEmployeesByIdentityIdIInfiniteQuery,
  );

export const useGetEmployeesByRoleIInfiniteQuery = normalizeInfiniteQueryData(
  employeesApi.useGetEmployeesByRoleIInfiniteQuery,
);

export const useGetEmployeesAllInfiniteQuery = normalizeInfiniteQueryData(
  employeesApi.useGetEmployeesAllIInfiniteQuery,
);

export const useGetEmployeesAccessibleByOrganizationQuery =
  normalizeInfiniteQueryData(
    employeesApi.useGetEmployeesAccessibleByOrganizationInfiniteQuery,
  );

const getCacheEmployeeByIdentityIdSelector = cachedItemByIdSelector(
  'employeesApi',
  'getEmployeesAllI',
  'identityId',
);

export const useGetEmployeeByIdentityIdSelector = id =>
  useSelector(getCacheEmployeeByIdentityIdSelector(id));
